import { useEffect, useRef, useState, useCallback } from 'react';
import { GetSelectList, DataResponse } from 'api/types';
import './style.scss';

type AsyncListProps<T> = {
  className?: string;
  loadingText?: string | React.ReactNode;
  loadedText?: string | React.ReactNode;
  getSelectListFunc?: (params: GetSelectList) => Promise<DataResponse<T>>;
  loadOptions?: (inputValue: string, prevOptions: T[], additional: { page: number }) => Promise<{ options: T[]; hasMore: boolean; additional: { page: number } }>;
  onSelect?: (selected: T[]) => void;
  renderItem?: (item: T) => React.ReactNode;
  getItemValue?: (item: T) => string | number;
  getItemText?: (item: T) => string;
  wrapContent?: boolean;
  wrapList?: boolean;
  wrapListItems?: boolean;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  endElementRef?: React.RefObject<HTMLDivElement>;
};

function AsyncList<T>({ className = '', loadedText = '', loadingText = '', getSelectListFunc, loadOptions, onSelect, renderItem, getItemValue = (item: any) => (item as any).value, getItemText = (item: any) => (item as any).text, wrapContent = true, wrapList = true, wrapListItems = true, scrollContainerRef, endElementRef }: AsyncListProps<T>): JSX.Element {
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  const localWrapperRef = useRef<HTMLDivElement>(null);
  const localEndRef = useRef<HTMLDivElement>(null);

  const wrapperRef = scrollContainerRef || localWrapperRef;
  const endRef = endElementRef || localEndRef;

  const pageRef = useRef(1);
  const hasMoreRef = useRef(true);
  const loadingRef = useRef(false);
  const itemsRef = useRef<T[]>([]);
  const initialLoadDoneRef = useRef(false);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  useEffect(() => {
    initialLoadDoneRef.current = initialLoadDone;
  }, [initialLoadDone]);

  const loadPage = useCallback(async () => {
    if (loadingRef.current || !hasMoreRef.current) return;

    loadingRef.current = true;
    setLoading(true);

    try {
      let newItems: T[] = [];
      let totalCount = 0;

      if (getSelectListFunc) {
        const res = await getSelectListFunc({ filters: { text: '' }, paging: { page: pageRef.current, pageSize: 20 } });
        newItems = res.rows;
        totalCount = res.totalCount;
      } else if (loadOptions) {
        const res = await loadOptions('', itemsRef.current, { page: pageRef.current });
        newItems = res.options;
        totalCount = itemsRef.current.length + newItems.length + (res.hasMore ? 1 : 0);
      }

      const combined = [...itemsRef.current, ...newItems];
      setItems(combined);
      itemsRef.current = combined;

      hasMoreRef.current = combined.length < totalCount;
      if (hasMoreRef.current) pageRef.current += 1;
      setInitialLoadDone(true);
    } finally {
      loadingRef.current = false;
      setLoading(false);
    }
  }, [getSelectListFunc, loadOptions]);

  const tryLoadMore = useCallback(() => {
    if (hasMoreRef.current && !loadingRef.current) {
      loadPage();
    }
  }, [loadPage]);

  const isEndVisible = useCallback(() => {
    const end = endRef.current;
    const wrapper = wrapperRef.current;
    if (!end || !wrapper) return false;
    const endRect = end.getBoundingClientRect();
    const wrapperRect = wrapper.getBoundingClientRect();
    return endRect.top <= wrapperRect.bottom && endRect.bottom >= wrapperRect.top;
  }, [endRef, wrapperRef]);

  useEffect(() => {
    if (!loading && (!initialLoadDone || isEndVisible())) {
      tryLoadMore();
    }
  }, [loading, initialLoadDone, tryLoadMore, isEndVisible]);

  useEffect(() => {
    const end = endRef.current;
    if (!end) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          tryLoadMore();
        }
      },
      { threshold: 0, root: wrapperRef.current || null },
    );

    observer.observe(end);
    return () => observer.disconnect();
  }, [endRef, wrapperRef, tryLoadMore]);

  const listItems = (
    <>
      {items.map((item) => (
        !renderItem ?
          <div key={getItemValue(item)} className='item'>
            <label>
              <span>{getItemText(item)}</span>
            </label>
          </div> :
          renderItem(item)
      ))}
      {loadingText && loading && <div className='async-list-loading'>{loadingText}</div>}
      {loadedText && !loading && !hasMoreRef.current && (
        <div className='async-list-all-items-loaded'>{loadedText}</div>
      )}
    </>
  );

  const listContent = wrapListItems ? (<div className="list">{listItems}</div>) : (listItems);

  const wrapperContent = wrapList ?
    (
      <div ref={wrapperRef} className='list-wrapper'>
        {listContent}
        {!endElementRef && <div ref={localEndRef} className="async-list-end-item" />}
      </div>
    ) : (
      listContent
    );

  return wrapContent ? (<div className={`async-list ${className}`.trim()}>{wrapperContent} </div>) : (wrapperContent);
};

export default AsyncList;
